import { PdfBrandConfig } from '@common/types';

import { Content, PageOrientation, TDocumentDefinitions } from 'pdfmake/interfaces';
import { PartialSubmission } from 'types';
import { getPdfHeaderByBrand } from './get-pdf-header-by-brand';

export interface PdfContentProps {
  content: Content;
  pageOrientation?: PageOrientation;
  header?: (currentPage: any, pageCount: any, pageSize: any) => Content[];
  footer?: (currentPage: any, pageCount: any, pageSize: any) => Content[];
  showPageNumbers?: boolean;
  submission?: PartialSubmission | null;
  brandConfig: PdfBrandConfig;
}

export async function convertImageToBase64(imgPath: string): Promise<string> {
  return new Promise((resolve, reject) => {
    if (!imgPath || imgPath.trim() === '') {
      resolve('');
      return;
    }

    const img = new Image();
    img.src = imgPath;
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Canvas not supported'));
        return;
      }

      ctx.drawImage(img, 0, 0);
      const base64 = canvas.toDataURL('image/png').split(',')[1];
      if (base64) {
        resolve(base64);
      } else {
        reject(new Error(`Image load error: ${imgPath}`));
      }
    };
    img.onerror = (err) => reject(new Error(`Image load error: ${err}`));
  });
}

export async function convertFontToBase64(fontPath: string): Promise<string> {
  const response = await fetch(fontPath);
  if (!response.ok) {
    throw new Error(`Font load error: ${fontPath} (status: ${response.status})`);
  }
  const arrayBuffer = await response.arrayBuffer();
  let binary = '';
  const bytes = new Uint8Array(arrayBuffer);
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

export const getPdfContentWithTemplate = async ({
  content,
  pageOrientation = 'portrait',
  header,
  footer,
  submission,
  brandConfig,
}: PdfContentProps): Promise<TDocumentDefinitions> => {
  const headerByBrand = getPdfHeaderByBrand(brandConfig, submission);
  // Load logo as base64 string with error handling
  const headerImage =
    !header && brandConfig.pdfHeaderBgImage ? await convertImageToBase64(brandConfig.pdfHeaderBgImage) : undefined;
  const footerImage =
    !footer && brandConfig.pdfFooterBgImage ? await convertImageToBase64(brandConfig.pdfFooterBgImage) : undefined;

  const brandHeader = (_currentPage: any, _pageCount: any, pageSize: any) =>
    headerByBrand ??
    (headerImage
      ? [
          {
            // if you specify both width and height - image will be stretched
            image: `data:image/png;base64,${headerImage}`,
            width: pageSize.width,
            // width: 150,
          },
        ]
      : []);

  const brandFooter = (currentPage: any, _pageCount: any, pageSize: any): Content[] => {
    const footerContent: Content[] = [];

    if (footerImage) {
      footerContent.push({
        image: `data:image/png;base64,${footerImage}`,
        width: pageSize.width,
        height: 20,
        ...brandConfig.style.footerStyle,
      });
    }

    if (brandConfig.showPageNumbers) {
      footerContent.push({
        text: `${currentPage}`,
        alignment: 'right',
        fontSize: 10,
        margin: [0, -15, 30, 0],
      });
    }

    return footerContent;
  };

  return {
    pageMargins: brandConfig.style.pageMargins,
    defaultStyle: {
      font: 'Inter', // Use your custom font here
      fontSize: 11,
      lineHeight: 1.2,
    },
    pageOrientation,

    header: header || brandHeader,
    footer: footer || brandFooter,
    content,
  };
};
